import { DataMartField, FieldType, isDatamartField, PlayImporterCSVField } from '@r1-compass-library/models';

export type ComparatorSet = {
    type: string;
    comparators: ComparatorMetadata[];
};

export type ComparatorMetadata = {
    label: string;
    value: string;
    valueType: ComparatorValueType;
};

export type ComparatorValueType =
    | 'nothing'
    | 'text'
    | 'textarea'
    | 'multipick'
    | 'date'
    | 'timestamptz'
    | 'date&date'
    | 'timeInterval'
    | 'text&text'
    | 'picklist'
    | 'typeahead';

export type ComparatorMap = Record<FieldType, ComparatorMetadata[]>;

export const picklistStringComparators = [
    { label: 'Blank', value: 'blank', valueType: 'nothing' },
    { label: 'Empty', value: 'isNull', valueType: 'nothing' },
    { label: 'Equals', value: 'equals', valueType: 'picklist' },
    { label: 'Starts With', value: 'startsWith', valueType: 'text' },
    { label: 'Ends With', value: 'endsWith', valueType: 'text' },
    { label: 'Contains', value: 'contains', valueType: 'text' },
    { label: 'In List', value: 'in', valueType: 'textarea' },
] as const satisfies ComparatorMetadata[];

export const fieldStringComparators = [
    { label: 'Blank', value: 'blank', valueType: 'nothing' },
    { label: 'Empty', value: 'isNull', valueType: 'nothing' },
    { label: 'Equals', value: 'equals', valueType: 'text' },
    { label: 'Starts With', value: 'startsWith', valueType: 'text' },
    { label: 'Ends With', value: 'endsWith', valueType: 'text' },
    { label: 'Contains', value: 'contains', valueType: 'text' },
    { label: 'In List', value: 'in', valueType: 'textarea' },
] as const satisfies ComparatorMetadata[];

export const typeaheadStringComparators = [
    { label: 'Blank', value: 'blank', valueType: 'nothing' },
    { label: 'Equals', value: 'equals', valueType: 'typeahead' },
    { label: 'Starts With', value: 'startsWith', valueType: 'text' },
    { label: 'Ends With', value: 'endsWith', valueType: 'text' },
    { label: 'Contains', value: 'contains', valueType: 'text' },
    { label: 'In List', value: 'in', valueType: 'textarea' },
] as const satisfies ComparatorMetadata[];

export const multipicklistComparators = [
    { label: 'In List', value: 'in', valueType: 'multipick' },
] as const satisfies ComparatorMetadata[];

export const dateComparators = [
    { label: 'Blank', value: 'blank', valueType: 'nothing' },
    { label: 'Empty', value: 'isNull', valueType: 'nothing' },
    { label: 'Exactly On', value: 'equals', valueType: 'date' },
    { label: 'Between', value: 'between', valueType: 'date&date' },
    { label: 'Before', value: 'lessThan', valueType: 'date' },
    { label: 'On or Before', value: 'lessThanOrEqualTo', valueType: 'date' },
    { label: 'After', value: 'greaterThan', valueType: 'date' },
    {
        label: 'On or After',
        value: 'greaterThanOrEqualTo',
        valueType: 'date',
    },
    {
        label: 'Within the Last',
        value: 'withinTheLast',
        valueType: 'timeInterval',
    },
    {
        label: 'Within the Next',
        value: 'withinTheNext',
        valueType: 'timeInterval',
    },
] as const satisfies ComparatorMetadata[];

export const numericComparators = [
    { label: 'Blank', value: 'blank', valueType: 'nothing' },
    { label: 'Between', value: 'between', valueType: 'text&text' },
    { label: 'Empty', value: 'isNull', valueType: 'nothing' },
    { label: '=', value: 'equals', valueType: 'text' },
    { label: '>', value: 'greaterThan', valueType: 'text' },
    { label: '<', value: 'lessThan', valueType: 'text' },
    { label: '<=', value: 'lessThanOrEqualTo', valueType: 'text' },
    { label: '>=', value: 'greaterThanOrEqualTo', valueType: 'text' },
] as const satisfies ComparatorMetadata[];

export const genericAndModelComparatorMap = {
    picklist: picklistStringComparators,
    multipicklist: multipicklistComparators,
    calendar: dateComparators,
    typeaheadsearch: typeaheadStringComparators,
    field: {
        varchar: fieldStringComparators,
        numeric: numericComparators,
        number: numericComparators,
        integer: numericComparators,
        date: dateComparators,
        currency: numericComparators,
    },
    metric: {
        varchar: fieldStringComparators,
        numeric: numericComparators,
        number: numericComparators,
        integer: numericComparators,
    },
};

export function lookupComparators(field: DataMartField | PlayImporterCSVField): ComparatorMetadata[] {
    if (isDatamartField(field)) {
        switch (field.fieldType?.toLowerCase()) {
            case 'picklist':
                return genericAndModelComparatorMap.picklist;
            case 'multipicklist':
                return genericAndModelComparatorMap.multipicklist;
            case 'calendar':
                return genericAndModelComparatorMap.calendar;
            case 'typeaheadsearch':
                return genericAndModelComparatorMap.typeaheadsearch;
            case 'field':
                switch (field.dataType?.toLowerCase()) {
                    case 'varchar':
                        return genericAndModelComparatorMap.field.varchar;
                    case 'integer':
                        return genericAndModelComparatorMap.field.integer;
                    case 'number':
                        return genericAndModelComparatorMap.field.number;
                    case 'numeric':
                        return genericAndModelComparatorMap.field.numeric;
                    case 'date':
                        return genericAndModelComparatorMap.field.date;
                    case 'timestamptz':
                        return genericAndModelComparatorMap.field.date;
                    default:
                        return [];
                }
            case 'metric':
                switch (field.dataType?.toLowerCase()) {
                    case 'varchar':
                        return genericAndModelComparatorMap.field.varchar;
                    case 'integer':
                        return genericAndModelComparatorMap.field.integer;
                    case 'number':
                        return genericAndModelComparatorMap.field.number;
                    case 'numeric':
                        return genericAndModelComparatorMap.field.numeric;
                    default:
                        return [];
                }
            default:
                return [];
        }
    } else {
        // Casting needed since `toLowerCase()` makes any string count rather than our string union
        //     which causes any garbage to be accepted as a case.
        const fieldType: PlayImporterCSVField['dataType'] =
            field.dataType?.toLowerCase() as PlayImporterCSVField['dataType'];
        switch (fieldType) {
            case 'integer':
                return genericAndModelComparatorMap.field.integer;
            case 'numeric':
                return genericAndModelComparatorMap.field.numeric;
            case 'varchar':
                return genericAndModelComparatorMap.field.varchar;
            case 'date':
                return genericAndModelComparatorMap.field.date;
            case 'timestamptz':
                return genericAndModelComparatorMap.field.date;
            default:
                return [];
        }
    }
}
export type FieldMergeSourceType = 'tfs' | 'library content' | 'file header';

export class FieldMergeGroup {
    value?: string;
    displayName?: string;
}
export class FieldMerge {
    label?: string;
    value?: string;
    source?: FieldMergeSourceType;
    options?: string;
}
export interface DynamicContent {
    group?: FieldMergeGroup;
    elements?: FieldMerge[];
}
